import {useEffect, useState} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'

import {UPDATE_KANBAN_STAGE} from '../../../gql/mutations/kanban'
import {IUpdateKanbanStageRes, IKanbanStage} from '../../../types/kanban'

interface Props {
  show: boolean
  stage?: IKanbanStage
  onClose: () => void
  onUpdated: () => void
}

export const ModalEditKanbanStage: React.FC<Props> = ({show, stage, onClose, onUpdated}) => {
  const intl = useIntl()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState<string>('')
  const [isClosedState, setIsClosedState] = useState(false)

  useEffect(() => {
    if (stage && show) {
      setName(stage.name)
      setDescription(stage.description || '')
      setColor(stage.color || '')
      setIsClosedState(!!stage.isClosedState)
    }
  }, [stage, show])

  const [updateStage, {loading}] = useMutation<IUpdateKanbanStageRes>(UPDATE_KANBAN_STAGE)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stage?.id) return
    try {
      const {data} = await updateStage({
        variables: {
          id: stage.id,
          name,
          description: description || null,
          color: color || null,
          isClosedState,
        },
      })
      if (!data?.updateKanbanStage?.ok) {
        toast.error(data?.updateKanbanStage?.errors?.[0] || intl.formatMessage({id: 'leads.kanban.errUpdatingColumn', defaultMessage: 'Error updating the column'}))
        return
      }
      toast.success(intl.formatMessage({id: 'leads.kanban.columnUpdated', defaultMessage: 'Column updated successfully'}))
      onUpdated()
      onClose()
    } catch (error) {
      console.error(error)
      toast.error(intl.formatMessage({id: 'leads.kanban.errUpdatingColumn', defaultMessage: 'Error updating the column'}))
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered className='kanban-stage-modal'>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className='kanban-stage-modal__header'>
          <Modal.Title><FormattedMessage id='leads.kanban.editColumn' defaultMessage='Edit column' /></Modal.Title>
        </Modal.Header>
        <Modal.Body className='kanban-stage-modal__body'>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label><FormattedMessage id='common.field.name' defaultMessage='Name' /></Form.Label>
            <Form.Control
              className='kanban-stage-modal__input'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label><FormattedMessage id='common.field.description' defaultMessage='Description' /></Form.Label>
            <Form.Control
              className='kanban-stage-modal__input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label><FormattedMessage id='common.field.color' defaultMessage='Color' /></Form.Label>
            <div className='kanban-color-picker'>
              <Form.Control
                className='kanban-stage-modal__input kanban-stage-modal__input--color'
                type='color'
                value={color || '#ffffff'}
                onChange={(e) => setColor(e.target.value)}
              />
              <Button variant='link' size='sm' onClick={() => setColor('')}>
                <FormattedMessage id='leads.kanban.noColor' defaultMessage='No color' />
              </Button>
            </div>
          </Form.Group>
          <Form.Check
            className='kanban-stage-modal__checkbox'
            type='switch'
            id='kanban-stage-edit-closed'
            label={intl.formatMessage({id: 'leads.kanban.isClosedState', defaultMessage: 'Is closed state?'})}
            checked={isClosedState}
            onChange={(e) => setIsClosedState(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer className='kanban-stage-modal__footer'>
          <Button variant='light' onClick={onClose} disabled={loading}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? intl.formatMessage({id: 'leads.kanban.saving', defaultMessage: 'Saving...'}) : intl.formatMessage({id: 'common.action.save', defaultMessage: 'Save'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
